import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, Box, Package, Loader2, ChevronRight, Printer, CheckCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { generatePickList } from '../../lib/services/binInventoryService';
import { getAdminId } from '../../utils/getAdminId';
import type { BinPickSuggestion } from '../../types/warehouse';
import { useAuth } from '../../contexts/AuthContext';

interface PickListItem {
  product_id: string;
  product_name?: string;
  quantity: number;
}

interface PickListDisplayProps {
  items: PickListItem[];
  title?: string;
  showPrintButton?: boolean;
  onPrint?: () => void;
  compact?: boolean;
}

export const PickListDisplay: React.FC<PickListDisplayProps> = ({
  items,
  title = 'Pick List',
  showPrintButton = false,
  onPrint,
  compact = false,
}) => {
  const { user } = useAuth();
  const [pickSuggestions, setPickSuggestions] = useState<BinPickSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [pickedItems, setPickedItems] = useState<Set<string>>(new Set());

  const loadPickList = useCallback(async () => {
    const adminId = getAdminId(user);
    if (items.length === 0 || !user || !adminId) {
      setPickSuggestions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const suggestions = await generatePickList(
        items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        adminId
      );
      setPickSuggestions(suggestions);
    } finally {
      setLoading(false);
    }
  }, [items, user]);

  useEffect(() => {
    loadPickList();
  }, [loadPickList]);

  const togglePicked = (productId: string) => {
    setPickedItems((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  const handlePrint = () => {
    if (onPrint) {
      onPrint();
      return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Pick List</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { font-size: 18px; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f5f5f5; }
          .location { font-family: monospace; background: #e5e7eb; padding: 2px 6px; border-radius: 4px; }
          .no-bin { color: #ef4444; }
          .checkbox { width: 20px; height: 20px; border: 2px solid #000; margin-right: 10px; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>${title}</h1>
        <table>
          <thead>
            <tr>
              <th style="width: 30px;"></th>
              <th>#</th>
              <th>Product</th>
              <th>SKU</th>
              <th>Qty</th>
              <th>Bin Location</th>
            </tr>
          </thead>
          <tbody>
            ${pickSuggestions
              .map(
                (s, idx) => `
              <tr>
                <td><div class="checkbox"></div></td>
                <td>${idx + 1}</td>
                <td>${s.product_name}</td>
                <td>${s.product_sku}</td>
                <td>${s.requested_quantity}</td>
                <td>${
                  s.bin_location.bin_id
                    ? `<span class="location">${s.bin_location.full_address}</span>`
                    : '<span class="no-bin">No bin assigned</span>'
                }</td>
              </tr>
            `
              )
              .join('')}
          </tbody>
        </table>
        <script>window.print(); window.close();</script>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
        <span className="ml-2 text-sm text-gray-500">Loading pick locations...</span>
      </div>
    );
  }

  if (pickSuggestions.length === 0) {
    return null;
  }

  const allPicked = pickedItems.size === pickSuggestions.length;
  const groupedByLocation = pickSuggestions.reduce((acc, item) => {
    const key = item.bin_location.full_address || 'Unassigned';
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(item);
    return acc;
  }, {} as Record<string, BinPickSuggestion[]>);

  if (compact) {
    return (
      <div className="space-y-2">
        {pickSuggestions.map((suggestion) => (
          <div
            key={suggestion.product_id}
            className="flex items-center justify-between text-sm"
          >
            <span className="text-gray-700 dark:text-gray-300">
              {suggestion.product_name}
            </span>
            {suggestion.bin_location.bin_id ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">
                <MapPin className="h-3 w-3" />
                {suggestion.bin_location.bin_code}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                <Box className="h-3 w-3" />
                No bin
              </span>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              ({pickSuggestions.length} items)
            </span>
          </div>
          <div className="flex items-center gap-2">
            {showPrintButton && (
              <Button variant="secondary" size="sm" onClick={handlePrint}>
                <Printer className="h-4 w-4 mr-1.5" />
                Print
              </Button>
            )}
            {allPicked && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                <CheckCircle className="h-3 w-3" />
                All picked
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {Object.entries(groupedByLocation).map(([location, items]) => (
          <div key={location}>
            <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900/50">
              <div className="flex items-center gap-2">
                {location === 'Unassigned' ? (
                  <>
                    <Box className="h-4 w-4 text-amber-500" />
                    <span className="text-sm font-medium text-amber-600 dark:text-amber-400">
                      No bin assigned
                    </span>
                  </>
                ) : (
                  <>
                    <MapPin className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {location}
                    </span>
                  </>
                )}
              </div>
            </div>
            {items.map((suggestion, idx) => (
              <div
                key={suggestion.product_id}
                onClick={() => togglePicked(suggestion.product_id)}
                className={`px-4 py-3 flex items-center gap-3 cursor-pointer transition-colors ${
                  pickedItems.has(suggestion.product_id)
                    ? 'bg-green-50 dark:bg-green-900/20'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
              >
                <div
                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                    pickedItems.has(suggestion.product_id)
                      ? 'border-green-500 bg-green-500 text-white'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                >
                  {pickedItems.has(suggestion.product_id) && (
                    <CheckCircle className="h-4 w-4" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div
                    className={`font-medium ${
                      pickedItems.has(suggestion.product_id)
                        ? 'text-green-700 dark:text-green-400 line-through'
                        : 'text-gray-900 dark:text-gray-100'
                    }`}
                  >
                    {suggestion.product_name}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    SKU: {suggestion.product_sku}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <span
                    className={`px-3 py-1 rounded-lg text-sm font-medium ${
                      pickedItems.has(suggestion.product_id)
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                        : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                    }`}
                  >
                    x{suggestion.requested_quantity}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

interface InlinePickLocationProps {
  productId: string;
  binLocations: Map<string, { bin_code: string; full_address: string }>;
}

export const InlinePickLocation: React.FC<InlinePickLocationProps> = ({
  productId,
  binLocations,
}) => {
  const location = binLocations.get(productId);

  if (!location) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
        <Box className="h-3 w-3" />
        No bin
      </span>
    );
  }

  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400"
      title={location.full_address}
    >
      <MapPin className="h-3 w-3" />
      {location.bin_code}
    </span>
  );
};
